import { ArrowRight, MessageSquare } from 'lucide-react';
import type { ActivityFilter, ObjectActivityEntry } from '@/lib/objectActivity';
import { filterActivity } from '@/lib/objectActivity';
import { messageTime, type MessageActions } from '@/lib/conversation';
import { MessageThread } from './Message';
import { Timeline } from './Timeline';
import { EntityText } from './EntityText';
import { EmptyState } from './EmptyState';
import { Button } from './Button';

export function ObjectActivity({
  entries,
  filter = 'all',
  expanded,
  onExpandedChange,
  onReply,
  onEdit,
  canEdit,
  ...actions
}: MessageActions & {
  entries: readonly ObjectActivityEntry[];
  filter?: ActivityFilter;
  expanded: ReadonlySet<string>;
  onExpandedChange: (id: string, value: boolean) => void;
  onReply?: (entry: Extract<ObjectActivityEntry, { kind: 'comment' }>) => void;
  onEdit?: (entry: Extract<ObjectActivityEntry, { kind: 'comment' }>) => void;
  canEdit?: (entry: Extract<ObjectActivityEntry, { kind: 'comment' }>) => boolean;
}) {
  const visible = filterActivity(entries, filter);
  if (!visible.length)
    return (
      <EmptyState
        icon={<MessageSquare />}
        title={filter === 'history' ? 'Изменений пока нет' : 'Обсуждение ещё не началось'}
        description="Здесь появятся комментарии и изменения этой записи."
      />
    );
  return (
    <ol
      data-slot="object-activity"
      aria-label="Комментарии и история"
      className="min-w-0 divide-y divide-line"
    >
      {visible.map((entry) => (
        <li key={entry.id} data-activity-id={entry.id}>
          {entry.kind === 'comment' ? (
            <>
              <MessageThread
                thread={entry.thread}
                expanded={expanded.has(entry.id)}
                onExpandedChange={(value) => onExpandedChange(entry.id, value)}
                {...actions}
              />
              {(onReply || (onEdit && canEdit?.(entry))) && (
                <div className="mb-4 ml-11 flex flex-wrap gap-1 pl-4">
                  {onReply && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => onReply(entry)}>
                      Ответить
                    </Button>
                  )}
                  {onEdit && canEdit?.(entry) && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(entry)}>
                      Изменить
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div data-slot="object-change" className="px-6 py-5">
              <Timeline
                items={[
                  {
                    id: entry.id,
                    kind: 'change',
                    time: messageTime(entry.at, actions.timeZone),
                    dateTime: entry.at,
                    timeTitle: messageTime(entry.at, actions.timeZone, true),
                    title: (
                      <span className="text-sm">
                        {actions.onEntityOpen ? (
                          <EntityText
                            name={entry.actor.name}
                            onClick={(event) =>
                              actions.onEntityOpen?.({ ...entry.actor, kind: 'user' }, event.currentTarget)
                            }
                          />
                        ) : (
                          <span className="font-medium">{entry.actor.name}</span>
                        )}{' '}
                        · изменения записи
                      </span>
                    ),
                    description: (
                      <dl className="mt-2 space-y-2">
                        {entry.changes.map((change) => (
                          <div key={change.key} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <dt className="text-xs text-fg-muted">{change.label}</dt>
                            <dd className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-fg">
                              <span className="text-fg-muted">
                                <span className="sr-only">Было: </span>
                                {change.before}
                              </span>
                              <ArrowRight className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
                              <span>
                                <span className="sr-only">Стало: </span>
                                {change.after}
                              </span>
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
